import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { noWhitespaceValidator, uniqueNameValidator } from '@common';
import {
  CommissioningDeviceAssignValidationParams,
  CommissioningStore,
  DeviceAssignmentResult,
  DeviceAssignmentType,
  GatewayStore,
  InstallationFeaturesStore,
  RemoteDeviceStore,
} from '@store';
import {
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
} from '@vi-iot/client';
import { Observable, Subject, takeUntil } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { NoSpecialCharactersValidator } from 'src/app/shared/validators/special-characters/special-characters.validator';

import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';

@Component({
  selector: 'app-commissioning-final-result-modal',
  templateUrl: './commissioning-final-result-modal.component.html',
  styleUrls: ['./commissioning-final-result-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningFinalResultModalComponent implements OnInit, OnDestroy {
  DeviceAssignmentType = DeviceAssignmentType;
  isSuccess = false;
  assignmentValidationResult: CommissioningDeviceAssignValidationParams[] = null;

  deviceName$: Observable<Feature<DeviceNameProperties, DeviceNameCommands>>;
  deviceName: string;
  deviceId: string;
  allDevicesNamesInUse: string[] = [];

  devicesNamesForm: FormGroup;
  numberOfFormControls: number;

  ngUnsubscribe$: Subject<void> = new Subject<void>();
  FeatureNames = FeatureNames;
  pairedDeviceNameFeature$: Observable<Feature<unknown, unknown>>;
  selectedDeviceName: string;
  constructor(
    private commissioningService: CommissioningService,
    private commissioningStore: CommissioningStore,
    private dialogRef: MatDialogRef<CommissioningFinalResultModalComponent>,
    public installationFeaturesStore: InstallationFeaturesStore,
    private remoteDeviceStore: RemoteDeviceStore,
    private fb: FormBuilder,
    public gatewayStore: GatewayStore
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');
    this.pairedDeviceNameFeature$ = this.commissioningStore.pairedDeviceId$.pipe(
      switchMap((deviceId) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.deviceName,
          deviceId
        )
      )
    );
    this.commissioningService.selectedDevice$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedDevice) => {
        if (selectedDevice) {
          this.selectedDeviceName = selectedDevice.name ? selectedDevice.name : '';
        }
      });
    this.commissioningStore.assignmentResult$
      .pipe(
        filter((entry) => !!entry),
        take(1)
      )
      .subscribe((assignmentResult) => {
        this.isSuccess = assignmentResult === DeviceAssignmentResult.Success;
      });

    this.commissioningStore.assignmentValidationResult$
      .pipe(
        filter((entry) => !!entry),
        take(1)
      )
      .subscribe((_assignmentValidationResult) => {
        this.assignmentValidationResult = _assignmentValidationResult;
        this.numberOfFormControls = this.assignmentValidationResult.length;
      });
    this.initDeviceNameForm();
  }

  initDeviceNameForm(): void {
    this.remoteDeviceStore.namesInUse$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((namesInUse) => {
        this.allDevicesNamesInUse = [...namesInUse];
      });

    const formControlsArray: FormControl[] = [];

    for (let i = 0; i < this.numberOfFormControls; i++) {
      const formControl = new FormControl('', [
        noWhitespaceValidator(),
        Validators.maxLength(40),
        uniqueNameValidator(this.allDevicesNamesInUse),
        NoSpecialCharactersValidator(),
      ]);

      formControlsArray.push(formControl);
    }

    this.devicesNamesForm = this.fb.group({
      devicesNamesFormArray: this.fb.array(formControlsArray),
    });
  }

  get devicesNamesFormArray(): any {
    return this.devicesNamesForm.get('devicesNamesFormArray') as FormArray;
  }

  saveDeviceName(): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.deviceName,
      this.devicesNamesForm.value.devicesNamesFormArray[0].trim(),
      FeaturePropertyKey.value,
      FeaturePropertyKey.name,
      FeatureCommands.setName,
      null,
      this.deviceId
    );
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }

  addAnotherComponent(): void {
    this.commissioningService.modalAction$.next(ModalAction.NavigateToStart);
  }

  trimDeviceName(name: string): string {
    return name.trim();
  }

  saveFhtChannelName(channelId: string, deviceId: string, i: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.deviceName,
      this.devicesNamesForm.value.devicesNamesFormArray[i].trim(),
      FeaturePropertyKey.value,
      FeaturePropertyKey.name,
      FeatureCommands.setName,
      null,
      channelId ? channelId : deviceId
    );
  }

  deviceAssignRetry(): void {
    this.commissioningStore.resetDeviceAssignState();
    this.commissioningService.modalAction$.next(ModalAction.NavigateToDeviceAssign);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
